import { BLOCK_GP_MASTER, GP_NAME_ALIASES } from '../constants/field-execution-master-data';
import { FIELD_EXECUTION_BLOCKS } from '../services/field-execution.service';
import { Project } from '../models/project.model';

export type DuplicateMatchMode = 'skip' | 'update' | 'allow';

/** Known sub-village spelling variants (lowercase) → canonical form for duplicate matching. */
export const SUB_VILLAGE_ALIASES: Record<string, string> = {
    ichhada: 'ichada',
    ichada:  'ichada',
    sadabrata: 'sadabrata',
};

const FE_BLOCKS = new Set<string>(FIELD_EXECUTION_BLOCKS);

function normalizePart(value: string): string {
    return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

export function normalizeBlockName(block: string): string {
    const b = normalizePart(block);
    if (b === 'dhamnagar') return 'Dhamanagar';
    if (b === 'dhamanagar nac' || b === 'dhamnagar nac') return 'Dhamanagar NAC';
    if (b === 'tihidi nac') return 'Tihidi NAC';
    if (b === 'dhusuri nac') return 'Dhusuri NAC';
    return block.trim() || '';
}

function normalizeBlock(block: string): string {
    return normalizePart(normalizeBlockName(block));
}

function isKnownGpForBlock(name: string, block: string): boolean {
    const n = normalizePart(name);
    if (!n || !block) return false;
    const master = BLOCK_GP_MASTER[block] ?? [];
    if (master.some(g => normalizePart(g) === n)) return true;
    const alias = GP_NAME_ALIASES[n];
    return !!(alias && master.some(g => normalizePart(g) === normalizePart(alias)));
}

export function inferBlockForGp(gp: string): string | undefined {
    const n = normalizePart(gp);
    if (!n) return undefined;
    const names = [n, GP_NAME_ALIASES[n]].filter(Boolean) as string[];
    for (const candidate of names) {
        for (const [block, gps] of Object.entries(BLOCK_GP_MASTER)) {
            if (gps.some(g => normalizePart(g) === normalizePart(candidate))) return block;
        }
    }
    return undefined;
}

/** Resolve block, GP, and sub-village from a Firestore project (handles legacy field layouts). */
export function resolveGpAndSubVillage(p: Project): { block: string; gp: string; sub: string } {
    let block = normalizeBlockName(p.block ?? '');
    const explicitGp = (p.gp ?? '').trim();
    const village = (p.village ?? '').trim();
    const loc = (p.loc ?? '').trim();

    if (!block && explicitGp) {
        block = inferBlockForGp(explicitGp) ?? '';
    }
    if (!block) {
        block = inferBlockForGp(village) ?? inferBlockForGp(loc) ?? '';
    }

    if (block && explicitGp && isKnownGpForBlock(explicitGp, block)) {
        return {
            block,
            gp: explicitGp,
            sub: loc && !isKnownGpForBlock(loc, block) ? loc : (village || explicitGp),
        };
    }

    if (block && isKnownGpForBlock(village, block)) {
        return {
            block,
            gp: village,
            sub: loc && !isKnownGpForBlock(loc, block) ? loc : village,
        };
    }
    if (block && isKnownGpForBlock(loc, block)) {
        return { block, gp: loc, sub: village || loc };
    }
    return { block, gp: village || loc, sub: loc || village };
}

export function canonicalizeSubVillage(name: string): string {
    const key = normalizePart(name).replace(/\s+gp$/i, '').trim();
    return SUB_VILLAGE_ALIASES[key] ?? key;
}

export function normalizeProjectName(name: string): string {
    return normalizePart(name)
        .replace(/["""''`]/g, '')
        .replace(/[.,;:!?()[\]/\\-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** True when newName is a renamed version of oldName (e.g. X → X_2). Update mode only. */
export function isProjectRenameVariant(oldName: string, newName: string): boolean {
    const o = normalizeProjectName(oldName);
    const n = normalizeProjectName(newName);
    if (o === n) return true;
    const stripSuffix = (s: string) => s.replace(/[_\s-]+v?\d+$/i, '').trim();
    if (stripSuffix(n) === o || stripSuffix(o) === n) return true;
    if (n.startsWith(`${o} `) || n.startsWith(`${o}_`)) return true;
    if (o.startsWith(`${n} `) || o.startsWith(`${n}_`)) return true;
    return false;
}

export function isSubVillageSpellingVariant(a: string, b: string): boolean {
    const ca = canonicalizeSubVillage(a);
    const cb = canonicalizeSubVillage(b);
    if (ca === cb) return true;
    if (ca.length < 4 || cb.length < 4) return false;
    if (Math.abs(ca.length - cb.length) > 1) return false;
    return levenshtein(ca, cb) <= 1;
}

function levenshtein(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    const dp = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
        }
    }
    return dp[m][n];
}

function geoMatches(lookup: DuplicateLookup, p: Project): boolean {
    const { block, gp, subVillage } = lookup;
    const geo = resolveGpAndSubVillage(p);
    const blockN = normalizeBlock(block);
    const pBlock = normalizeBlock(geo.block);
    if (pBlock && blockN && pBlock !== blockN) return false;
    if (normalizePart(geo.gp) !== normalizePart(gp)) return false;
    return isSubVillageSpellingVariant(subVillage, geo.sub);
}

/** Block + GP + sub-village + project name — strict duplicate identity (Skip mode). */
export function buildProjectDuplicateKey(
    block: string,
    gp: string,
    subVillage: string,
    projectName: string
): string {
    return [
        normalizeBlock(block),
        normalizePart(gp),
        canonicalizeSubVillage(subVillage || gp),
        normalizeProjectName(projectName),
    ].join('|');
}

export function duplicateKeyFromProject(p: Project): string {
    const { block, gp, sub } = resolveGpAndSubVillage(p);
    return buildProjectDuplicateKey(block, gp, sub, p.name);
}

export function subVillageFromImportRow(gp: string, villageName?: string): string {
    return villageName?.trim() ? villageName : gp;
}

export function buildWithinSheetRowKey(subVillage: string, projectName: string): string {
    return `${canonicalizeSubVillage(subVillage)}|${normalizeProjectName(projectName)}`;
}

/** Keep only Field Execution projects and normalise block spelling. */
export function filterFieldExecutionProjects(projects: Project[]): Project[] {
    return projects
        .map(p => {
            const geo = resolveGpAndSubVillage(p);
            return {
                ...p,
                block: geo.block || normalizeBlockName(p.block ?? ''),
            };
        })
        .filter(p => {
            const b = p.block ?? '';
            return FE_BLOCKS.has(b) || FE_BLOCKS.has(normalizeBlockName(b));
        });
}

export interface DuplicateLookup {
    block: string;
    gp: string;
    subVillage: string;
    projectName: string;
    scheme?: string;
}

/**
 * Index of existing projects for duplicate lookup.
 *
 * Modes:
 * - skip:   strict match (Block+GP+Village+Project Name) — duplicates are skipped
 * - update: strict match OR same location/rename → update existing; no match → create
 * - allow:  never matches — every row is imported as a new record
 */
export class ProjectDuplicateIndex {
    private readonly byExactKey = new Map<string, Project>();
    private readonly byId = new Map<string, Project>();
    private readonly projects: Project[] = [];

    static fromProjects(projects: Project[]): ProjectDuplicateIndex {
        const idx = new ProjectDuplicateIndex();
        for (const p of filterFieldExecutionProjects(projects)) idx.add(p);
        return idx;
    }

    add(p: Project): void {
        this.projects.push(p);
        this.byId.set(p.id, p);
        this.byExactKey.set(duplicateKeyFromProject(p), p);
    }

    /** After create/update so later rows in the same import see the new state. */
    registerAfterWrite(p: Project): void {
        const prev = this.byId.get(p.id);
        if (prev) {
            const oldKey = duplicateKeyFromProject(prev);
            if (this.byExactKey.get(oldKey)?.id === p.id) this.byExactKey.delete(oldKey);
            const idx = this.projects.findIndex(x => x.id === p.id);
            if (idx >= 0) this.projects[idx] = p;
        } else {
            this.projects.push(p);
        }
        this.byId.set(p.id, p);
        this.byExactKey.set(duplicateKeyFromProject(p), p);
    }

    registerKey(key: string, p: Project): void {
        this.byExactKey.set(key, p);
        if (!this.byId.has(p.id)) {
            this.projects.push(p);
            this.byId.set(p.id, p);
        }
    }

    has(lookup: DuplicateLookup, mode: DuplicateMatchMode = 'skip'): boolean {
        return !!this.find(lookup, mode);
    }

    find(lookup: DuplicateLookup, mode: DuplicateMatchMode = 'skip'): Project | undefined {
        if (mode === 'allow') return undefined;

        const strict = this.findStrict(lookup);
        if (strict) return strict;

        if (mode === 'update') return this.findForUpdate(lookup);
        return undefined;
    }

    /** Block + GP + Village + exact Project Name (Skip / first pass for Update). */
    private findStrict(lookup: DuplicateLookup): Project | undefined {
        const { block, gp, subVillage, projectName, scheme } = lookup;
        const direct = buildProjectDuplicateKey(block, gp, subVillage, projectName);
        const exact = this.byExactKey.get(direct);
        if (exact) return exact;

        const blockN = normalizeBlock(block);
        const gpN = normalizePart(gp);
        const projN = normalizeProjectName(projectName);
        const targetSub = canonicalizeSubVillage(subVillage);

        const candidates = this.projects.filter(p => {
            const geo = resolveGpAndSubVillage(p);
            const pBlock = normalizeBlock(geo.block);
            if (pBlock && blockN && pBlock !== blockN) return false;
            if (normalizePart(geo.gp) !== gpN) return false;
            return normalizeProjectName(p.name) === projN;
        });

        if (candidates.length === 1) return candidates[0];

        if (candidates.length > 1) {
            const bySub = candidates.filter(p =>
                isSubVillageSpellingVariant(targetSub, resolveGpAndSubVillage(p).sub)
            );
            if (bySub.length === 1) return bySub[0];

            if (scheme?.trim()) {
                const schemeN = normalizePart(scheme);
                const byScheme = candidates.filter(p => normalizePart(p.scheme ?? '') === schemeN);
                if (byScheme.length === 1) return byScheme[0];
            }
        }

        for (const p of this.projects) {
            const geo = resolveGpAndSubVillage(p);
            if (normalizePart(geo.gp) !== gpN) continue;
            if (normalizeProjectName(p.name) !== projN) continue;
            const pBlock = normalizeBlock(geo.block);
            if (pBlock && blockN && pBlock !== blockN) continue;
            if (isSubVillageSpellingVariant(targetSub, geo.sub)) return p;
        }

        const gpSub = canonicalizeSubVillage(gp);
        if (targetSub !== gpSub) {
            for (const p of this.projects) {
                const geo = resolveGpAndSubVillage(p);
                if (normalizePart(geo.gp) !== gpN) continue;
                if (normalizeProjectName(p.name) !== projN) continue;
                const pBlock = normalizeBlock(geo.block);
                if (pBlock && blockN && pBlock !== blockN) continue;
                const existingSub = canonicalizeSubVillage(geo.sub);
                if (existingSub === gpSub || !p.loc?.trim()) return p;
            }
        }

        return undefined;
    }

    /**
     * Update mode: match same Block+GP+Village (+ scheme) even when project name changed (X → X_2).
     */
    private findForUpdate(lookup: DuplicateLookup): Project | undefined {
        const { projectName, scheme } = lookup;
        const byLocation = this.projects.filter(p => geoMatches(lookup, p));

        if (scheme?.trim()) {
            const schemeN = normalizePart(scheme);
            const byScheme = byLocation.filter(p => normalizePart(p.scheme ?? '') === schemeN);
            if (byScheme.length === 1) return byScheme[0];
            const renamed = byScheme.filter(p => isProjectRenameVariant(p.name, projectName));
            if (renamed.length === 1) return renamed[0];
        }

        if (byLocation.length === 1) return byLocation[0];

        const renamed = byLocation.filter(p => isProjectRenameVariant(p.name, projectName));
        if (renamed.length === 1) return renamed[0];

        return undefined;
    }
}
